/*
  UJV4DAWN-696: enhanced mobile sticky buy bar.

  Mirrors the in-page add-to-bag button (label, disabled state) and the rendered price block,
  so a variant change keeps the bar in sync without duplicating any pricing logic. Visibility is
  driven by an IntersectionObserver on the real button: the bar is only shown on mobile while
  that button is off screen.
*/
if (!customElements.get('sticky-buy-bar')) {
  customElements.define(
    'sticky-buy-bar',
    class StickyBuyBar extends HTMLElement {
      constructor() {
        super();
        this.mediaQuery = window.matchMedia('(max-width: 768px)');
        this.targetIsVisible = true;
        this.forceHidden = false;
        this.onClick = this.onClick.bind(this);
        this.onBreakpointChange = this.onBreakpointChange.bind(this);
        this.onHideSticky = this.onHideSticky.bind(this);
        this.onShowSticky = this.onShowSticky.bind(this);
        this.scheduleSync = this.scheduleSync.bind(this);
      }

      connectedCallback() {
        this.submitButton = document.getElementById(this.dataset.targetButton);
        if (!this.submitButton) return;

        this.priceSource = document.getElementById(this.dataset.priceSource);
        this.button = this.querySelector('[data-sticky-submit]');
        this.label = this.querySelector('[data-sticky-label]');
        this.priceTargets = this.querySelectorAll('[data-sticky-price]');

        this.button.addEventListener('click', this.onClick);

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            this.targetIsVisible = entries[entries.length - 1].isIntersecting;
            this.updateVisibility();
          },
          { threshold: 0 }
        );
        this.intersectionObserver.observe(this.submitButton);

        // The label and the disabled state are written straight onto the real button by
        // VariantSelects (toggleAddButton / setUnavailable) and by the back in stock app, so
        // watching it covers every path that can change it.
        this.buttonObserver = new MutationObserver(this.scheduleSync);
        this.buttonObserver.observe(this.submitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'style'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        if (this.priceSource) {
          this.priceObserver = new MutationObserver(this.scheduleSync);
          this.priceObserver.observe(this.priceSource, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        if (typeof this.mediaQuery.addEventListener === 'function') {
          this.mediaQuery.addEventListener('change', this.onBreakpointChange);
        } else {
          this.mediaQuery.addListener(this.onBreakpointChange);
        }

        // Kept from the previous implementation: filterVariantImg in global.js still drives the
        // bar (and the real button) through these events on mobile only.
        if (this.mediaQuery.matches) {
          document.addEventListener('hideSticky', this.onHideSticky);
          document.addEventListener('showSticky', this.onShowSticky);
        }

        this.sync();
      }

      disconnectedCallback() {
        if (this.intersectionObserver) this.intersectionObserver.disconnect();
        if (this.buttonObserver) this.buttonObserver.disconnect();
        if (this.priceObserver) this.priceObserver.disconnect();
        if (this.resizeObserver) this.resizeObserver.disconnect();
        document.body.classList.remove('sticky-buy-bar-active');
        if (this.button) this.button.removeEventListener('click', this.onClick);
        if (typeof this.mediaQuery.removeEventListener === 'function') {
          this.mediaQuery.removeEventListener('change', this.onBreakpointChange);
        } else {
          this.mediaQuery.removeListener(this.onBreakpointChange);
        }
        document.removeEventListener('hideSticky', this.onHideSticky);
        document.removeEventListener('showSticky', this.onShowSticky);
        if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      }

      onClick(event) {
        event.preventDefault();
        if (this.submitButton.hasAttribute('disabled')) return;
        this.submitButton.click();
      }

      onBreakpointChange() {
        this.updateVisibility();
      }

      onHideSticky() {
        this.forceHidden = true;
        this.submitButton.style.opacity = 0.5;
        this.submitButton.disabled = true;
        this.updateVisibility();
      }

      onShowSticky() {
        this.forceHidden = false;
        this.submitButton.style.opacity = 1;
        this.submitButton.disabled = false;
        this.updateVisibility();
      }

      scheduleSync() {
        if (this.syncFrame) return;
        this.syncFrame = requestAnimationFrame(() => {
          this.syncFrame = null;
          this.sync();
        });
      }

      sync() {
        const labelSource = this.submitButton.querySelector('span');

        if (this.label && labelSource) {
          const text = labelSource.textContent.trim();
          if (text) this.label.textContent = text;
        }

        this.button.disabled = this.submitButton.hasAttribute('disabled');

        // The in-button price only makes sense next to "Add to Bag". Keying off the label rather
        // than the disabled state keeps it steady through the brief disable VariantSelects applies
        // while it fetches the new variant's markup.
        const addToCartLabel = (this.dataset.addToCartLabel || '').trim().toLowerCase();
        const label = this.label ? this.label.textContent.trim().toLowerCase() : '';
        this.classList.toggle('sticky-buy-bar--hide-price', label !== addToCartLabel);

        const price = this.currentPrice();
        if (price) {
          this.priceTargets.forEach((target) => {
            target.textContent = price;
          });
        }
      }

      // Reads back whichever price the customer is actually seeing, so sale pricing and the
      // shop's money format come along for free.
      currentPrice() {
        if (!this.priceSource) return null;
        const price = this.priceSource.querySelector('.price');
        if (!price) return null;

        const item = price.classList.contains('price--on-sale')
          ? price.querySelector('.price__sale .price-item--sale')
          : price.querySelector('.price__regular .price-item--regular');

        return item ? item.textContent.trim() : null;
      }

      updateVisibility() {
        const show = this.mediaQuery.matches && !this.forceHidden && !this.targetIsVisible;
        this.classList.toggle('sticky-buy-bar--visible', show);
        this.updateFooterClearance(show);
      }

      // VS2026-52: while the bar is up, the page gets bottom padding matching its height so the
      // customer can scroll the whole footer past it. Tied to the bar actually showing, so pages
      // where it never appears (sold out products) keep their usual bottom edge.
      updateFooterClearance(show) {
        if (show && !this.resizeObserver && 'ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.setClearanceHeight());
          this.resizeObserver.observe(this);
        }
        if (show) this.setClearanceHeight();
        document.body.classList.toggle('sticky-buy-bar-active', show);
      }

      setClearanceHeight() {
        document.body.style.setProperty('--sticky-buy-bar-height', `${this.offsetHeight}px`);
      }
    }
  );
}
